import { addDays } from 'date-fns';
import { ScanDate } from '../../../../../../types';
import { dateToScheduleFormat } from '../../../helpers/date';
import { makeSafeIsoDateString } from '../../../../../../libs/doneDate';

/**
 * How far from the scheduled date the pilot may state a flight date. Kept narrow, since the point is to
 * correct a clock that is hours or a day out; a day wider than the five the GPS branch tolerates.
 */
export const CONFIRM_DATE_WINDOW_DAYS = 7;

export type ConfirmDateBounds = {
  /** What the picker opens on. */
  initial: ScanDate;
  min: ScanDate;
  max: ScanDate;
};

const shiftDays = (date: ScanDate, days: number) =>
  dateToScheduleFormat(addDays(new Date(makeSafeIsoDateString(date)), days));

// ISO dates sort lexicographically, so string comparison is enough.
const earliest = (a: ScanDate, b: ScanDate) => (a < b ? a : b);
const clamp = (date: ScanDate, min: ScanDate, max: ScanDate) => (date < min ? min : date > max ? max : date);

/**
 * The range the pilot may state a flight date inside: `scheduled ± CONFIRM_DATE_WINDOW_DAYS`, clipped at
 * today, since a flight cannot have happened later.
 *
 * The picker opens on the scheduled date, not the one read from the file, so that a single click cannot
 * turn the unverified drone clock into a pilot-stated date.
 *
 * `min` is clipped to `max` as well, leaving a selectable day rather than an empty range when the flight
 * is scheduled more than a window out.
 */
export const resolveConfirmDateBounds = (scheduledDate: ScanDate, today: ScanDate): ConfirmDateBounds => {
  const max = earliest(shiftDays(scheduledDate, CONFIRM_DATE_WINDOW_DAYS), today);
  const min = earliest(shiftDays(scheduledDate, -CONFIRM_DATE_WINDOW_DAYS), max);

  return { initial: clamp(scheduledDate, min, max), min, max };
};
